import React from 'react';
import { Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EhiWindowSnapshots } from '@services/ehi.service.ts';

type EhiTrendPoint = EhiWindowSnapshots['items'][number];

interface EhiTrendCellProps {
  /** Вікно розрахунку і реальні знімки в ньому (з рядка узагальнених даних); null - запис ще не перераховано. */
  snapshots: EhiWindowSnapshots | null;
}

// Колонка "Динаміка" в "Узагальнених" (2026-09-22): міні-графік ДЕННИХ значень знімків за 30-денне вікно, з яких
// складається EHI (історії самого узагальненого EHI немає - 1 запис на користувача). Дані - з самого запису
// (`windowSnapshots`: межі вікна + лише реальні знімки, заповнює бекенд при перерахунку), окремих запитів нема.
// Напрямок руху (останній знімок проти попереднього) - окремо, індикатором біля числа EHI (EhiTrendIndicator), тут не
// дублюється.
// - Тонка доріжка на все вікно: дні без даних читаються як "даних не було" - і всередині, і на початку/наприкінці.
// - Послідовні дні - лінія; пропущений день - розрив, а не домальована точка; видима точка - лише поодинокий день (без
//   сусідів) і останній знімок.
// - Вісь Y фіксована 0-100 (шкала EHI): однаковий нахил означає однакову зміну в усіх рядках. Ширина фіксована - колонка
//   не "стрибає" між рядками.
// - Читабельність (після перегляду користувачем): легка заливка під лінією - форма руху видна з першого погляду; ледь
//   помітна середня лінія на 50 - вище/нижче середини шкали; більші точки.
// Колір нейтральний (`text.secondary`): колір рівня вже є в колонці "Рівень".
// Перша версія (від 3 знімків, 96 px, точка на кожному знімку) на скриншоті користувача виглядала погано - переверстано.
const WIDTH = 160;
const HEIGHT = 36;
const PAD = 4;
const DAY_MS = 24 * 60 * 60 * 1000;

const toDayNumber = (date: string) => Math.round(Date.parse(`${date}T00:00:00Z`) / DAY_MS);

export const EhiTrendCell: React.FC<EhiTrendCellProps> = ({ snapshots }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  if (!snapshots) {
    return (
      <Tooltip title={t('ehiJournal.trendNotCalculated')}>
        <Typography variant="body2" color="text.disabled" component="span">—</Typography>
      </Tooltip>
    );
  }

  const { dateFrom, dateTo, items: points } = snapshots;
  const last = points.length > 0 ? points[points.length - 1] : null;

  const formatDate = (date: string) =>
    new Date(`${date}T00:00:00`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });

  const dayFrom = toDayNumber(dateFrom);
  const daySpan = Math.max(1, toDayNumber(dateTo) - dayFrom);
  const x = (date: string) => PAD + ((toDayNumber(date) - dayFrom) / daySpan) * (WIDTH - PAD * 2);
  const y = (value: number) => PAD + (1 - Math.min(100, Math.max(0, value)) / 100) * (HEIGHT - PAD * 2);

  // Відрізки лише між сусідніми днями: день без знімка розриває лінію.
  const runs: EhiTrendPoint[][] = [];
  points.forEach((p, i) => {
    const isNextDay = i > 0 && toDayNumber(p.date) - toDayNumber(points[i - 1].date) === 1;
    if (isNextDay) runs[runs.length - 1].push(p);
    else runs.push([p]);
  });

  const lineColor = theme.palette.text.secondary;
  const fillColor = alpha(theme.palette.text.secondary, 0.12);
  const trackY = HEIGHT - PAD;

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={last
        ? t('ehiJournal.trendAria', { count: points.length, from: points[0].value, to: last.value })
        : t('ehiJournal.trendNoSnapshots')}
      style={{ overflow: 'visible', display: 'block' }}
    >
      {/* Доріжка - усе вікно: відрізки без лінії/точок над нею = дні без знімків. */}
      <line x1={PAD} y1={trackY} x2={WIDTH - PAD} y2={trackY} stroke={theme.palette.divider} strokeWidth={1} />
      {/* Середина шкали (50) - орієнтир "вище/нижче", найслабший елемент графіка. */}
      <line x1={PAD} y1={y(50)} x2={WIDTH - PAD} y2={y(50)} stroke={theme.palette.divider} strokeWidth={1} strokeDasharray="2 3" />
      {runs.map((run) => (run.length > 1 ? (
        <polygon
          key={`fill-${run[0].date}`}
          points={[
            `${x(run[0].date)},${trackY}`,
            ...run.map((p) => `${x(p.date)},${y(p.value)}`),
            `${x(run[run.length - 1].date)},${trackY}`,
          ].join(' ')}
          fill={fillColor}
        />
      ) : null))}
      {runs.map((run) => (run.length > 1 ? (
        <polyline
          key={run[0].date}
          points={run.map((p) => `${x(p.date)},${y(p.value)}`).join(' ')}
          fill="none"
          stroke={lineColor}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : null))}
      {points.map((p) => {
        const isolated = runs.some((run) => run.length === 1 && run[0] === p);
        return (
          <Tooltip key={p.date} title={`${formatDate(p.date)}: ${p.value}`} placement="top" disableInteractive>
            <g>
              {(isolated || p === last) && <circle cx={x(p.date)} cy={y(p.value)} r={p === last ? 4 : 3} fill={lineColor} />}
              {/* Зона наведення - більша за точку, є й на днях без видимої точки. */}
              <circle cx={x(p.date)} cy={y(p.value)} r={8} fill="transparent" />
            </g>
          </Tooltip>
        );
      })}
    </svg>
  );
};
